import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import find from 'lodash/find';
import { navigate } from 'gatsby'
import PropTypes from "prop-types";

import { getSeries } from '../api/series';
import Layout from '../components/Layout'
import SEO from '../components/seo'
import CountriesDropdown from "../components/CountriesDropdown";
import { getCountries } from "../api/dictionaries";
import UnifyDateField from "../components/UnifyDateField";
import StatisticsLayout from "../components/StatisticsLayout";
import FiltersContainer from "../components/FiltersContainer";
import ChartContainer from "../components/ChartContainer";
import FilerLegend from "../components/FilerLegend";
import ForPopulationField from "../components/ForPopulationField";
import Spacer from "../components/Spacer";
import ChartTypeField from "../components/ChartTypeField";
import ChartHeader from "../components/ChartHeader";
import getInitialChartParams from "../helpers/getInitialChartParams";
import ChartScaleField from "../components/ChartScaleField";
import SpinnerOverlay from "../components/SpinnerOverlay";
import ChartWrapper from "../components/ChartWrapper";
import DatepickerRangeField from "../components/DatepickerRangeField";
import formatDate from "../utils/formatDate";


const changeUrl = ({pathname, countriesString, chartType, unifyStartDay, perPopulation, fromDate, toDate, chartScale}) => {
  const newLocation = `/${pathname}?countries=${countriesString}&chartType=${chartType}&unifyStartDay=${unifyStartDay.checked}&unifyStartDayCount=${unifyStartDay.count}&perPopulation=${perPopulation}&fromDate=${fromDate}&toDate=${toDate}&scale=${chartScale}`;
  navigate(newLocation, { replace: true });
};

const IndexPage = ({ location }) => {
  const initialParams = getInitialChartParams(location.search);
  const [data, setData] = useState();
  const [pendingFetch, setPendingFetch] = useState(false);
  const pathname = useMemo(() => location.pathname, [location]);
  const [unifyStartDay, setUnifyStartDay] = useState(() => initialParams.unifyStartDay);
  const [perPopulation, setPerPopulation] = useState(() => initialParams.perPopulation);
  const [chartType, setChartType] = useState(() => initialParams.chartType);
  const [chartScale, setChartScale] = useState(initialParams.scale);
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState(() => initialParams.countries);
  const [fromDate, setFromDate] = useState(() => initialParams.fromDate);
  const [toDate, setToDate] = useState(() => initialParams.toDate);

  const countriesString = selectedCountries.length > 0 ? selectedCountries.join(',') : undefined;

  const fromDateString = useMemo(() => {
    return formatDate(fromDate);
  }, [fromDate])
  const toDateString = useMemo(() => {
    return formatDate(toDate)
  }, [toDate]);

  useEffect(() => {
    const fetchData = async () => {
      setPendingFetch(true)
      const result = await getSeries({
        countries: countriesString,
        chartType,
        unifyStartDay: unifyStartDay.checked,
        unifyStartDayCount: unifyStartDay.count,
        perPopulation,
        fromDate: fromDateString,
        toDate: toDateString,
      })
      setData(result)
      setPendingFetch(false)
      changeUrl({
        pathname,
        countriesString,
        chartType,
        unifyStartDay,
        perPopulation,
        fromDate: fromDateString,
        toDate: toDateString,
        chartScale,
      })
    }
    fetchData(countriesString)
  }, [
    pathname,
    countriesString,
    selectedCountries,
    unifyStartDay,
    perPopulation,
    chartType,
    fromDate,
    toDate,
  ])

  useEffect(() => {
    changeUrl({
      pathname,
      countriesString,
      chartType,
      unifyStartDay,
      perPopulation,
      fromDate: fromDateString,
      toDate: toDateString,
      chartScale,
    })
  }, [    pathname,
    countriesString,
    selectedCountries,
    unifyStartDay,
    perPopulation,
    chartType,
    fromDate,
    toDate,
    chartScale,
  ])

  useEffect(() => {
    const fetchCountries = async () => {
      const result = await getCountries();
      setCountries(result)
    };
    fetchCountries();
  },[]);

  const onChangePerPopulation = (e) => {
    setPerPopulation(e.target.checked)
  };

  return (
    <Layout>
      <SEO />
      <StatisticsLayout>
        <FiltersContainer>
          {countries.length > 0 && <CountriesDropdown options={countries.length ? countries.map(country => country.name) : []} selectedItems={selectedCountries} setSelectedItems={setSelectedCountries} />}
          <Spacer />
          <DatepickerRangeField fromDate={fromDate} onChangeFromDate={setFromDate} toDate={toDate} onChangeToDate={setToDate} />
          <Spacer />
          <ChartTypeField value={chartType} onChange={setChartType} />
          <ChartScaleField value={chartScale} onChange={setChartScale} />
          <FilerLegend>
            <ForPopulationField onChange={onChangePerPopulation} checked={perPopulation} />
            <Spacer />
            <UnifyDateField onChange={setUnifyStartDay} value={unifyStartDay} />
          </FilerLegend>
        </FiltersContainer>
        <ChartContainer>
          <ChartHeader type={chartType} countries={selectedCountries} location={location} />
          <ChartWrapper>
            {pendingFetch && <SpinnerOverlay />}
            {data && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis scale={chartScale} domain={chartScale && [0.01, 'auto']}  />
                  <Tooltip />
                  <Legend />
                  {
                    selectedCountries.map(country => {
                      const matchedCountry = find(countries, { name: country});
                      return (
                        <Line key={country} type="monotone" dataKey={country} stroke={matchedCountry ? `#${matchedCountry.color}` : '#000000'} />
                      )})
                  }
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartWrapper>
        </ChartContainer>
      </StatisticsLayout>
    </Layout>
  )
};

IndexPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
};

export default IndexPage
